import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getRcks, getShelves, getSpots } from "../../../server/InventoryUpdate";
import {
  destinationAreaTransferIssue,
  destinationAreaTransferMessage,
  filterCompatibleDestinationSpots,
  getSpotId,
  maxTransferQuantity,
} from "../../../utils/inventoryTransfer";

const padLocationPart = (value, len) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  const n = digits === "" ? 0 : parseInt(digits, 10);
  if (Number.isNaN(n)) return len === 2 ? "00" : "000";
  const cap = len === 2 ? 99 : 999;
  return String(Math.min(Math.max(n, 0), cap)).padStart(len, "0");
};

const locationPartDigits = (value) => String(value ?? "").replace(/\D/g, "");

const locationPartMatchesFilter = (partValue, filterValue) => {
  const filter = String(filterValue ?? "").trim();
  if (!filter) return true;
  const part = String(partValue ?? "").trim();
  if (part.includes(filter)) return true;
  const partDigits = locationPartDigits(part);
  const filterDigits = locationPartDigits(filter);
  if (!filterDigits) return part.includes(filter);
  if (!partDigits) return false;
  if (partDigits.startsWith(filterDigits)) return true;
  const partNum = partDigits.replace(/^0+/, "") || "0";
  const filterNum = filterDigits.replace(/^0+/, "") || "0";
  return partNum === filterNum;
};

const rackLabel = (rack) =>
  padLocationPart(locationPartDigits(rack.rackId) || rack.rackId, 2);

const shelfLabel = (shelf) => {
  const part = shelf.rackShelfId?.split("-")[1] ?? shelf.rackShelfId;
  return padLocationPart(locationPartDigits(part) || part, 2);
};

const spotLabel = (spot) => {
  const part = spot.rackShelfSpotId?.split("-")[2] ?? spot.rackShelfSpotId;
  return padLocationPart(locationPartDigits(part) || part, 3);
};

const formatSourceLabel = (row) => {
  if (row === -1) return "מיון";
  if (!row || typeof row !== "object") return "—";
  const zoneName = row?.zoneName || row?.zone?.name || row?.locationName || "";
  const spotId = row?.spotId || row?.id || "";
  const coords = String(spotId || "")
    .split("-")
    .slice(1)
    .map((part) => part.trim())
    .filter(Boolean)
    .join("-");
  if (zoneName && coords) return `${zoneName} | ${coords}`;
  return zoneName || coords || spotId || "—";
};

const TransferDestinationEditor = ({
  zones,
  productId,
  selectedSrc,
  productAvailability,
  onSubmit,
  submitting,
}) => {
  const destinationZones = useMemo(
    () => (Array.isArray(zones) ? zones : []),
    [zones],
  );
  const sourceSpotId = getSpotId(selectedSrc);

  const [selectedZone, setSelectedZone] = useState("");
  const [selectedRackId, setSelectedRackId] = useState("");
  const [selectedShelfId, setSelectedShelfId] = useState("");
  const [selectedSpot, setSelectedSpot] = useState("");
  const [cellInput, setCellInput] = useState("");
  const [racks, setRacks] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [spots, setSpots] = useState([]);
  const [loadingLevel, setLoadingLevel] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const cascadeGen = useRef(0);
  const initKeyRef = useRef("");
  const cellListId = useMemo(
    () => `transfer-cell-list-${productId || "default"}`,
    [productId],
  );

  const selectedZoneType = useMemo(
    () => destinationZones.find((z) => z.id === selectedZone)?.type,
    [destinationZones, selectedZone],
  );

  const transferMax = useMemo(() => {
    if (!selectedSrc || selectedSrc === -1) return Infinity;
    return maxTransferQuantity(
      selectedSrc,
      selectedZoneType,
      productAvailability,
    );
  }, [selectedSrc, selectedZoneType, productAvailability]);

  const sourceForSpots = selectedSrc && selectedSrc !== -1 ? selectedSrc : null;

  const destinationSpots = useMemo(() => {
    if (!selectedZone) return spots;
    return filterCompatibleDestinationSpots(
      spots,
      sourceForSpots || { spotId: sourceSpotId },
      selectedZone,
    );
  }, [spots, selectedZone, sourceForSpots, sourceSpotId]);

  const destinationIssueMessage = useMemo(
    () =>
      destinationAreaTransferMessage(
        destinationAreaTransferIssue({
          from: sourceForSpots,
          spots,
          zoneId: selectedZone,
          zoneSelected: Boolean(selectedZone),
          loading: Boolean(loadingLevel),
          racksCount: racks.length,
          shelvesCount: shelves.length,
          rackSelected: Boolean(selectedRackId),
          shelfSelected: Boolean(selectedShelfId),
        }),
      ),
    [
      sourceForSpots,
      spots,
      selectedZone,
      loadingLevel,
      racks.length,
      shelves.length,
      selectedRackId,
      selectedShelfId,
    ],
  );

  const sourceLabel = useMemo(
    () => formatSourceLabel(selectedSrc),
    [selectedSrc],
  );

  const helperText = useMemo(() => {
    if (!destinationZones.length) return "אין יעדים זמינים להעברה זו";
    if (!selectedZone) return "יש לבחור אזור יעד";
    if (loadingLevel) return "טוען מיקומים...";
    if (destinationIssueMessage) return destinationIssueMessage;
    if (!selectedRackId) return "יש לבחור טור יעד";
    if (!selectedShelfId) return "יש לבחור מדף יעד";
    if (!selectedSpot) return "יש לבחור או להזין מספר תא יעד";
    if (Number.isFinite(transferMax) && transferMax <= 0) {
      return "אין כמות פנויה להעברה מתא זה";
    }
    return "";
  }, [
    destinationZones.length,
    selectedZone,
    selectedRackId,
    selectedShelfId,
    selectedSpot,
    loadingLevel,
    transferMax,
    destinationIssueMessage,
  ]);

  const canSubmit =
    !!selectedZone &&
    !!selectedRackId &&
    !!selectedShelfId &&
    !!selectedSpot &&
    !destinationIssueMessage &&
    sourceSpotId !== selectedSpot &&
    quantity >= 1 &&
    (selectedSrc === -1 ||
      !Number.isFinite(transferMax) ||
      (transferMax > 0 && quantity <= transferMax)) &&
    !submitting &&
    !loadingLevel;

  const resetDestination = useCallback(() => {
    setSelectedZone("");
    setSelectedRackId("");
    setSelectedShelfId("");
    setSelectedSpot("");
    setCellInput("");
    setRacks([]);
    setShelves([]);
    setSpots([]);
    setLoadingLevel(null);
  }, []);

  useEffect(() => {
    if (!selectedSrc || selectedSrc === -1) return;
    if (!Number.isFinite(transferMax) || transferMax <= 0) return;
    setQuantity((current) => {
      const n = Number(current);
      if (!Number.isFinite(n) || n <= 0) return transferMax;
      if (n > transferMax) return transferMax;
      return current;
    });
  }, [transferMax, selectedSrc]);

  const applyQuantityDefault = useCallback(
    (zoneType) => {
      if (selectedSrc === -1) {
        setQuantity(1);
        return;
      }
      if (!selectedSrc) return;
      const maxQty = maxTransferQuantity(
        selectedSrc,
        zoneType,
        productAvailability,
      );
      setQuantity(Number.isFinite(maxQty) ? Math.max(0, maxQty) : 1);
    },
    [selectedSrc, productAvailability],
  );

  const loadRacksForZone = useCallback(async (zoneId) => {
    const gen = ++cascadeGen.current;
    setSelectedZone(zoneId);
    setSelectedRackId("");
    setSelectedShelfId("");
    setSelectedSpot("");
    setCellInput("");
    setRacks([]);
    setShelves([]);
    setSpots([]);
    if (!zoneId) {
      setLoadingLevel(null);
      return;
    }

    setLoadingLevel("racks");
    try {
      const racksRes = await getRcks(zoneId);
      if (gen !== cascadeGen.current) return;
      setRacks(racksRes?.data || []);
    } catch (err) {
      console.error("Error loading destination racks:", err);
    } finally {
      if (gen === cascadeGen.current) setLoadingLevel(null);
    }
  }, []);

  useEffect(() => {
    if (!destinationZones.length || selectedSrc == null) return;
    const srcKey =
      selectedSrc === -1
        ? "miyun"
        : String(selectedSrc.id ?? selectedSrc.spotId);
    const zonesKey = destinationZones.map((z) => z.id).join(",");
    const initKey = `${srcKey}|${productId}|${zonesKey}`;
    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;

    applyQuantityDefault(undefined);
    resetDestination();
  }, [
    selectedSrc,
    destinationZones,
    productId,
    applyQuantityDefault,
    resetDestination,
  ]);

  const handleZoneChange = (zoneId) => {
    const zoneType = destinationZones.find((z) => z.id === zoneId)?.type;
    applyQuantityDefault(zoneType);
    void loadRacksForZone(zoneId);
  };

  const handleRackChange = async (rackId) => {
    const gen = ++cascadeGen.current;
    setSelectedRackId(rackId);
    setSelectedShelfId("");
    setSelectedSpot("");
    setCellInput("");
    setShelves([]);
    setSpots([]);
    if (!rackId || !selectedZone) return;

    setLoadingLevel("shelves");
    try {
      const shelvesRes = await getShelves(`${selectedZone}-${rackId}`);
      if (gen !== cascadeGen.current) return;
      setShelves(shelvesRes?.data || []);
    } catch (err) {
      console.error("Error loading shelves:", err);
    } finally {
      if (gen === cascadeGen.current) setLoadingLevel(null);
    }
  };

  const handleShelfChange = async (shelfId) => {
    const gen = ++cascadeGen.current;
    setSelectedShelfId(shelfId);
    setSelectedSpot("");
    setCellInput("");
    setSpots([]);
    if (!shelfId || !selectedZone) return;

    setLoadingLevel("spots");
    try {
      const spotsRes = await getSpots(`${selectedZone}-${shelfId}`);
      if (gen !== cascadeGen.current) return;
      setSpots(spotsRes?.data || []);
    } catch (err) {
      console.error("Error loading spots:", err);
    } finally {
      if (gen === cascadeGen.current) setLoadingLevel(null);
    }
  };

  const resolveTypedCell = (text) => {
    const trimmed = String(text ?? "").trim();
    if (!selectedShelfId || !trimmed || !selectedZone) return null;
    const matches = destinationSpots.filter((spot) => {
      const cellPart = spot.rackShelfSpotId?.split("-")[2] ?? "";
      return locationPartMatchesFilter(cellPart, trimmed);
    });
    if (matches.length === 1) {
      return `${selectedZone}-${matches[0].rackShelfSpotId}`;
    }
    if (matches.length > 1) {
      const padded = padLocationPart(locationPartDigits(trimmed), 3);
      const exact = matches.find((s) => spotLabel(s) === padded);
      if (exact) return `${selectedZone}-${exact.rackShelfSpotId}`;
    }
    return null;
  };

  const handleCellInputChange = (raw) => {
    const digits = locationPartDigits(raw).slice(0, 3);
    setCellInput(digits);
    const resolved = resolveTypedCell(digits);
    setSelectedSpot(resolved || "");
  };

  const handleCellBlur = () => {
    if (!cellInput) return;
    const resolved = resolveTypedCell(cellInput);
    if (resolved) {
      setSelectedSpot(resolved);
      const cellPart = resolved.split("-").pop() ?? "";
      setCellInput(padLocationPart(locationPartDigits(cellPart), 3));
    }
  };

  const handleQuantityChange = (raw) => {
    if (raw === "") {
      setQuantity("");
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    if (selectedSrc === -1 || !Number.isFinite(transferMax)) {
      setQuantity(Math.max(1, n));
      return;
    }
    if (transferMax <= 0) {
      setQuantity(0);
      return;
    }
    setQuantity(Math.min(Math.max(1, n), transferMax));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      to: selectedSpot,
      quantity: Number(quantity),
      destinationZoneType: selectedZoneType,
    });
  };

  return (
    <div className="transfer-destination-editor">
      <div className="transfer-destination-editor__source" role="group">
        <span className="transfer-destination-editor__source-label">מקור</span>
        <span
          className="transfer-destination-editor__source-value"
          title={sourceLabel}
        >
          {sourceLabel}
        </span>
      </div>

      <div className="transfer-destination-editor__dest-title">יעד</div>

      <div className="transfer-destination-editor__table" role="table">
        <div className="transfer-destination-editor__head" role="row">
          <div role="columnheader">מיקום</div>
          <div role="columnheader">כמות</div>
        </div>

        <div className="transfer-destination-editor__row" role="row">
          <div
            className="transfer-destination-editor__location"
            role="cell"
            data-label="מיקום"
          >
            <label className="transfer-destination-editor__field">
              <span>אזור</span>
              <select
                value={selectedZone}
                onChange={(e) => handleZoneChange(e.target.value)}
                disabled={!destinationZones.length}
                aria-label="אזור יעד"
              >
                <option value="">בחר אזור</option>
                {!destinationZones.length && (
                  <option value="" disabled>
                    אין אזורים זמינים
                  </option>
                )}
                {destinationZones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="transfer-destination-editor__field">
              <span>טור</span>
              <select
                value={selectedRackId}
                onChange={(e) => handleRackChange(e.target.value)}
                disabled={!selectedZone || loadingLevel === "racks"}
              >
                <option value="">בחר טור</option>
                {racks.map((rack) => (
                  <option key={String(rack.rackId)} value={rack.rackId}>
                    {rackLabel(rack)}
                  </option>
                ))}
              </select>
            </label>

            <label className="transfer-destination-editor__field">
              <span>מדף</span>
              <select
                value={selectedShelfId}
                onChange={(e) => handleShelfChange(e.target.value)}
                disabled={!selectedRackId || !!loadingLevel}
              >
                <option value="">בחר מדף</option>
                {shelves.map((shelf) => (
                  <option
                    key={String(shelf.rackShelfId)}
                    value={shelf.rackShelfId}
                  >
                    {shelfLabel(shelf)}
                  </option>
                ))}
              </select>
            </label>

            <label className="transfer-destination-editor__field">
              <span>תא</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={3}
                list={cellListId}
                placeholder="בחר / הקלד"
                value={cellInput}
                onChange={(e) => handleCellInputChange(e.target.value)}
                onBlur={handleCellBlur}
                disabled={!selectedShelfId || !!loadingLevel}
                aria-label="תא יעד"
              />
              <datalist id={cellListId}>
                {destinationSpots.map((spot) => {
                  const value = `${selectedZone}-${spot.rackShelfSpotId}`;
                  const label = spotLabel(spot);
                  return (
                    <option key={value} value={label}>
                      {label}
                    </option>
                  );
                })}
              </datalist>
            </label>
          </div>

          <div
            className="transfer-destination-editor__qty"
            role="cell"
            data-label="כמות"
          >
            <label className="transfer-destination-editor__field">
              <span aria-hidden="true">&nbsp;</span>
              <input
                type="number"
                min={1}
                max={Number.isFinite(transferMax) ? transferMax : undefined}
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                disabled={Number.isFinite(transferMax) && transferMax <= 0}
                aria-label="כמות להעברה"
              />
            </label>
          </div>
        </div>
      </div>

      {helperText ? (
        <p className="transfer-destination-editor__hint">{helperText}</p>
      ) : null}

      <button
        type="button"
        className="ui-btn-brand transfer-destination-editor__submit"
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        העבר
      </button>
    </div>
  );
};

export default TransferDestinationEditor;
